import React from "react";
import { motion } from "framer-motion";
import { Instagram, Facebook, Phone, Mail, MapPin, Clock } from "lucide-react";
import { BRAND, LOGOS, whatsappHref } from "../data/content";
import { useBooking } from "./BookingModal";

const Footer = () => {
  const { openBooking } = useBooking();

  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="relative overflow-hidden border-t border-[color:var(--border)] bg-black"
    >
      {/* HUGE display headline */}
      <div className="mx-auto max-w-[1600px] px-6 pt-24 md:px-12 md:pt-40">
        <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <div className="chip mb-6">
              <span className="dot" />
              06 · Come see us
            </div>
            <motion.h2
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.7, 0, 0.2, 1] }}
              className="font-serif text-6xl leading-[0.85] tracking-tight md:text-[9vw]"
            >
              Let&rsquo;s begin
              <br />
              <span className="italic-serif text-[color:var(--primary)]">
                the ritual.
              </span>
            </motion.h2>
          </div>
          <div className="md:col-span-4">
            <button
              data-testid="footer-book-btn"
              onClick={openBooking}
              className="group relative inline-flex h-40 w-40 items-center justify-center rounded-full border border-[color:var(--primary)] bg-[color:var(--primary)]/10 transition hover:bg-[color:var(--primary)] md:h-56 md:w-56"
            >
              <span className="font-serif text-xl italic md:text-2xl">
                Book →
              </span>
              <span className="absolute inset-0 animate-[spin_18s_linear_infinite]">
                <svg viewBox="0 0 200 200" className="h-full w-full">
                  <defs>
                    <path
                      id="c"
                      d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0"
                    />
                  </defs>
                  <text
                    fill="currentColor"
                    className="fill-[color:var(--muted)] font-mono text-[10px] uppercase tracking-[0.3em]"
                  >
                    <textPath href="#c">
                      Start the journey · Book via Instagram or WhatsApp ·
                    </textPath>
                  </text>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Info grid */}
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-12 border-t border-[color:var(--border)] px-6 py-16 md:grid-cols-4 md:px-12 md:py-20">
        <InfoBlock
          icon={<MapPin size={16} />}
          label="Visit"
          testid="footer-visit"
        >
          <a
            href="https://maps.google.com/?q=99+Salon+and+Academy+Patiala"
            target="_blank"
            rel="noopener noreferrer"
            className="link-underline"
          >
            {BRAND.address}
          </a>
        </InfoBlock>

        <InfoBlock
          icon={<Phone size={16} />}
          label="Call"
          testid="footer-call"
        >
          {BRAND.phones.map((p) => (
            <a
              key={p}
              href={`tel:+91${p}`}
              className="block link-underline"
              data-testid={`footer-phone-${p}`}
            >
              +91 {p.slice(0, 5)} {p.slice(5)}
            </a>
          ))}
          <a
            href={whatsappHref()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-whatsapp"
            className="mt-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--primary)]"
          >
            WhatsApp →
          </a>
        </InfoBlock>

        <InfoBlock
          icon={<Mail size={16} />}
          label="Write"
          testid="footer-email"
        >
          <a
            href={`mailto:${BRAND.email}`}
            className="link-underline break-all"
          >
            {BRAND.email}
          </a>
        </InfoBlock>

        <InfoBlock
          icon={<Clock size={16} />}
          label="Hours"
          testid="footer-hours"
        >
          {BRAND.timings}
          <br />
          <span className="text-[color:var(--muted)]">Open all days</span>
        </InfoBlock>
      </div>

      {/* Bottom band */}
      <div className="border-t border-[color:var(--border)] px-6 py-8 md:px-12">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <img
              src={LOGOS.main}
              alt="99"
              className="h-9 w-auto"
            />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)]">
              © {new Date().getFullYear()} · {BRAND.short}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <SocialLink
              testid="footer-instagram"
              href={BRAND.instagram}
              icon={<Instagram size={16} />}
              label={BRAND.instagramHandle}
            />
            <SocialLink
              testid="footer-facebook"
              href={BRAND.facebook}
              icon={<Facebook size={16} />}
              label={BRAND.facebookHandle}
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

const InfoBlock = ({ icon, label, children, testid }) => (
  <div data-testid={testid}>
    <div className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)]">
      <span className="text-[color:var(--primary)]">{icon}</span>
      {label}
    </div>
    <div className="font-serif text-lg leading-relaxed md:text-xl">{children}</div>
  </div>
);

const SocialLink = ({ href, icon, label, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group flex items-center gap-3 border border-[color:var(--border)] px-4 py-2.5 transition hover:border-[color:var(--primary)]"
  >
    <span className="text-[color:var(--primary)]">{icon}</span>
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[color:var(--muted)] group-hover:text-[color:var(--text)]">
      {label}
    </span>
  </a>
);

export default Footer;
